import type { FC } from "react";
import { useEffect } from "react";
import { useStore } from "@tanstack/react-form";

import {
  FieldDatepicker,
  FieldInput,
  FieldMultipleCheckbox,
} from "@/components/form";
import FormComponentTitle from "@/components/form/form-component-title";
import type { UpdateFieldProps } from "@/components/form/patient/update-form";
import { Separator } from "@/components/ui/separator";
import { updatePatientSchema } from "@/schemas/patient";
import {
  allergiesOptions,
  pathologiesOptions,
  womenHealthOptions,
} from "@/utils/patient";

const GeneralHealthForm: FC<UpdateFieldProps> = ({ formApi }) => {
  const allergiesValues = useStore(
    formApi.store,
    (state) => state.values.allergies
  );

  const womenHealthValues = useStore(
    formApi.store,
    (state) => state.values.womenHealth
  );

  const associatedPathologiesValues = useStore(
    formApi.store,
    (state) => state.values.associatedPathologies
  );

  useEffect(() => {
    if (allergiesValues?.includes("OTHER")) return;
    formApi.setFieldValue("allergiesComment", "");
  }, [allergiesValues]);

  useEffect(() => {
    if (!womenHealthValues?.includes("OTHER")) {
      formApi.setFieldValue("womenHealthComment", "");
    }
    if (!womenHealthValues?.includes("PREGNANT")) {
      formApi.setFieldValue("pregnancyEnd", null);
    }
  }, [womenHealthValues]);

  return (
    <div className="flex flex-col gap-4 w-full">
      <FormComponentTitle
        title="Santé générale"
        subTitle="Ajouter les informations du patient"
      />
      <Separator />
      <div className="flex gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Pathologies associées
        </label>
        <FieldMultipleCheckbox
          Field={formApi.Field}
          schema={updatePatientSchema}
          name="associatedPathologies"
          options={pathologiesOptions}
          validators={{
            onChange: updatePatientSchema.shape.associatedPathologies,
          }}
        />
        {associatedPathologiesValues?.includes("OTHER") ? (
          <FieldInput
            Field={formApi.Field}
            schema={updatePatientSchema}
            name="pathologyComment"
            placeholder="Ajouter un commentaire"
            validators={{
              onChange: updatePatientSchema.shape.pathologyComment,
            }}
          />
        ) : null}
      </div>
      <Separator />
      <div className="flex gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Allergies
        </label>
        <div className="flex flex-col gap-4 w-full lg:max-w-[512px]">
          <FieldMultipleCheckbox
            Field={formApi.Field}
            schema={updatePatientSchema}
            name="allergies"
            options={allergiesOptions}
            validators={{
              onChange: updatePatientSchema.shape.allergies,
            }}
          />
          {allergiesValues?.includes("OTHER") ? (
            <FieldInput
              Field={formApi.Field}
              schema={updatePatientSchema}
              name="allergiesComment"
              placeholder="Ajouter un commentaire"
              className="ml-[50%]"
              validators={{
                onChange: updatePatientSchema.shape.allergiesComment,
              }}
            />
          ) : null}
        </div>
      </div>
      <Separator />
      <div className="flex gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Santé de la femme
        </label>
        <div className="flex flex-col gap-4 w-full lg:max-w-[512px]">
          <FieldMultipleCheckbox
            Field={formApi.Field}
            schema={updatePatientSchema}
            options={womenHealthOptions}
            name="womenHealth"
            validators={{
              onChange: updatePatientSchema.shape.womenHealth,
            }}
          />
          <div className="flex flex-col sm:flex-row gap-1">
            {womenHealthValues?.includes("OTHER") ? (
              <FieldInput
                Field={formApi.Field}
                schema={updatePatientSchema}
                name="womenHealthComment"
                label="Commentaire"
                placeholder="Ajouter un commentaire"
                validators={{
                  onChange: updatePatientSchema.shape.womenHealthComment,
                }}
              />
            ) : null}
            {womenHealthValues?.includes("PREGNANT") ? (
              <FieldDatepicker
                Field={formApi.Field}
                schema={updatePatientSchema}
                name="pregnancyEnd"
                label="Date de fin de gestation"
                placeholder="Ajouter un commentaire"
                validators={{
                  onChange: updatePatientSchema.shape.pregnancyEnd,
                }}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralHealthForm;
